import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SegmentedControl, type SegmentedOption } from "@nexus/ui";
import type { ChangelogEntry } from "@/features/changelog/api/changelog.api";
import { useSprintConfig } from "@/features/changelog/hooks/useChangelogSettings";
import {
  buildTemplateWindows,
  formatWindow,
  isWithinWindow,
  type ChangelogTemplateId,
} from "@/features/changelog/lib/changelog-templates";

interface ChangelogDeployRangeFilterProps {
  /** Deploys before the range narrows them, so a chip can tell whether it has anything to show. */
  entries: ChangelogEntry[];
  /** Frozen by the caller, so the windows cannot shift under a page left open. */
  now: Date;
  value: ChangelogTemplateId | null;
  onChange: (value: ChangelogTemplateId | null) => void;
}

/** "All time" is a stretch like any other here, so it is a segment, not an absence. */
const ALL_TIME = "all";

/**
 * The same week, fortnight and sprint stretches a roundup is written from,
 * offered as a filter — "what shipped this sprint" is the question asked of
 * this page, and it was previously answered by counting columns on the chart.
 *
 * Shares `SegmentedControl` with the Events tab's time range: the two ask the
 * same question and now answer it in the same shape.
 */
export function ChangelogDeployRangeFilter({
  entries,
  now,
  value,
  onChange,
}: ChangelogDeployRangeFilterProps) {
  const { t, i18n } = useTranslation();
  const sprint = useSprintConfig();

  const options = useMemo<SegmentedOption[]>(() => {
    const stretches = buildTemplateWindows(now, i18n.language, sprint).map((template) => ({
      value: template.id,
      label:
        template.id === "sprint"
          ? template.title
          : t(`admin.changelog.filters.range.${template.id}`, { defaultValue: template.id }),
      title: formatWindow(template.window, i18n.language),
      // An empty stretch is not worth switching to, but the one already on
      // screen stays selectable so it can be switched away from.
      disabled:
        value !== template.id &&
        !entries.some((entry) => isWithinWindow(entry.deployedAt, template.window)),
    }));

    return [
      { value: ALL_TIME, label: t("admin.changelog.filters.range.all", "All time") },
      ...stretches,
    ];
  }, [entries, now, value, t, i18n.language, sprint]);

  return (
    <SegmentedControl
      value={value ?? ALL_TIME}
      onValueChange={(next) => onChange(next === ALL_TIME ? null : (next as ChangelogTemplateId))}
      options={options}
      ariaLabel={t("admin.changelog.filters.range.label", "Date range")}
    />
  );
}
